import React, { createContext, useContext, useEffect, useState } from 'react';
import Uppy from '@uppy/core';
import '@uppy/core/dist/style.css';
import '@uppy/dashboard/dist/style.css';
import Tus from '@uppy/tus';
import { supabase } from '@/services';

interface UppyContextProps {
  uppy: Uppy;
}

const UppyContext = createContext<UppyContextProps | undefined>(undefined);

interface UppyProviderProps {
  children: React.ReactNode;
}

export const supabaseUploadURL = `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/upload/resumable`;

export const UppyProvider: React.FC<UppyProviderProps> = ({ children }) => {
  const [uppy] = useState<Uppy>(
    new Uppy({
      id: 'imageUploader',
      autoProceed: true,
      allowMultipleUploadBatches: true,
      restrictions: {
        maxFileSize: 50 * 1024 * 1024, // 50MB
        maxNumberOfFiles: 10,
        allowedFileTypes: ['image/*'],
      },
    })
  );
  useEffect(() => {
    const initializeUppy = async () => {
      // Retrieve the current user's session for authentication
      const {
        data: { session },
      } = await supabase.auth.getSession();
      uppy.use(Tus, {
        endpoint: supabaseUploadURL,
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
          'x-upsert': 'true',
        },
        metadata: {
          source: 'image-uploader',
          cacheControl: '3600',
        },
        chunkSize: 6 * 1024 * 1024, // 6MB chunks as recommended by Supabase
        retryDelays: [0, 1000, 3000, 5000],
      });
    };
    // Initialize Uppy with Supabase settings
    initializeUppy();
  }, [uppy]);

  return <UppyContext.Provider value={{ uppy }}>{children}</UppyContext.Provider>;
};

export const useUppy = (): UppyContextProps => {
  const context = useContext(UppyContext);
  if (context === undefined) {
    throw new Error('useUppy must be used within an UppyProvider');
  }
  return context;
};
